import type { AvailabilityInputs, AvailabilityRule } from './availability'
import type { FgaAuthorizationRule } from './fgaRules'
import type { RoleAuthorizationContext, RoleAuthorizationRule } from './roleAuthorization'
import type { AuthzContextState } from './useAuthzContextQuery'
import type { AnyRecordAuthorization } from './useRecordAuthorization'
import type { RecordPermissionsEntity, RecordPermissionsFor } from '@omnea-digital/authz-model'

/** The verbs of one entity family. Narrowed to string so a generic entity's verb can still index its map. */
export type RecordVerb<Entity extends RecordPermissionsEntity> = keyof RecordPermissionsFor<Entity> & string

/** Construct only through `recordAuthorization`: `verb` is widened here, so a literal skips its entity check. */
type RecordAuthorizationRule = {
  kind: 'record'
  entity: RecordPermissionsEntity
  verb: string
  role: RoleAuthorizationRule
}

/** Which implementation decides, and what it reads. `open` is still subject to availability. */
export type AuthorizationRule =
  | { kind: 'open' }
  | { kind: 'fga'; fga: FgaAuthorizationRule }
  | { kind: 'fgaOrRole'; fga: FgaAuthorizationRule; role: RoleAuthorizationRule }
  | { kind: 'role'; role: RoleAuthorizationRule }
  | RecordAuthorizationRule

/**
 * Anything gated: whether it exists for the organisation at all, and who may reach it. A route is this
 * plus an address; every other surface is this alone. Availability is omitted where the surface exists
 * for every organisation, which is most of them.
 */
export type AccessRule = {
  availability?: AvailabilityRule
  authorization: AuthorizationRule
}

/** A rule the caller must name a record for; the hooks take that id as a second argument. */
export type RecordAccessRule = { availability?: AvailabilityRule; authorization: RecordAuthorizationRule }

/** A rule that answers without a record, so asking one for a record id would be meaningless. */
export type NonRecordAccessRule = {
  availability?: AvailabilityRule
  authorization: Exclude<AuthorizationRule, RecordAuthorizationRule>
}

/**
 * `pending` and `unavailable` are withheld answers rather than denials: a caller who has access must
 * not be redirected because a check had not finished or could not be made.
 */
export type AccessAnswer = 'allowed' | 'denied' | 'pending' | 'unavailable'

/** Every source, in the state the hooks hold them. */
export type AccessSources = {
  authz: AuthzContextState
  role: RoleAuthorizationContext
  availability: AvailabilityInputs
  record?: AnyRecordAuthorization
}

/**
 * One verb of one entity family. The rule names no record, so every site asking this question shares
 * it and the caller supplies the record id. The role arm decides where the service does not answer
 * for the record.
 */
export const recordAuthorization = <Entity extends RecordPermissionsEntity>(
  entity: Entity,
  verb: RecordVerb<Entity>,
  role: RoleAuthorizationRule
): RecordAuthorizationRule => ({ kind: 'record', entity, verb, role })
